import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import {
  checkMoneyDataForTable,
  checkNonMoneyDataForTable,
  trimObject,
} from "utils/utils";
import { searchErrorHandler } from "utils/formValidation";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import PayfacPrefixSearchFilter from "components/PayfacPrefixSearchField/PayfacPrefixSearchFilter";
import {
  usePayfacPrefixColumn,
} from "components/PayfacPrefixSearchField/payfacPrefixColumn";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import cardTypeOptions from "utils/cardTypeOptions/CardTypeOptions.jsx";
import cardOriginOptions from "utils/cardOriginOptions/CardOriginOptions.jsx";
import paymentGatewayOptions from "utils/paymentGatewayOptions/PaymentGatewayOptions.jsx";
import statusOptions from "utils/statusOptions/StatusOptions.jsx";
import ChangeContent from "./components/ChangeContent/ChangeContent";
import ChangeAllContent from "./components/ChangeAllContent/ChangeAllContent";
import CreateFormContent from "./components/CreateFormContent/CreateFormContent";
import s from "./All.module.scss";
import {
  changeAllPlan,
  changePlan,
  createForm,
  exportCsv,
  queryList,
} from "./model";
import { queryTableData } from "../apiUtils";

const All = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath, detailPageState } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "SettlementPlanAll";
  const payfacPrefixColumn = usePayfacPrefixColumn({ index: 1 });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState({
    id: "",
    payfacPrefix: "",
    merchantId: "",
    merchantName: "",
    bankAccount: "",
    beneficiary: "",
    currency: "",
    totalAmount: "",
    settleCurrency: "",
    adjustAmount: "",
    totalNet: "",
    startDate: null,
    endDate: null,
    startSettleDate: null,
    endSettleDate: null,
    cardType: "",
    cardOrigin: "",
    paymentGateway: "",
    status: "",
  });
  const [focus, setFocus] = useState({});
  const [error, setError] = useState({
    startDate: false,
    endDate: false,
    startDateLaterThanEndDate: false,
    startSettleDate: false,
    endSettleDate: false,
    settlementStartDateLaterThanEndDate: false,
    planStatus: false,
    settleDate: false,
    settleDateBeforeToday: false,
  });
  const [columnsArray, setColumnsArray] = useState([]);
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState(false);
  const [onchangeData, setOnChangeData] = useState({});
  const [changeDialogData, setChangeDialogData] = useState({
    adjustAmount: "",
    updateBankAccount: false,
    settleDate: "",
    changeReason: "",
  });
  const [isChangeAllDialogOpen, setIsChangeAllDialogOpen] = useState(false);
  const [changeAllDialogData, setChangeAllDialogData] = useState({
    updateBankAccount: false,
    settleDate: "",
    planStatus: "",
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDialogData, setCreateDialogData] = useState({
    merchantId: "",
    totalServiceFee: "",
    totalChangeFee: "",
    settleCurrency: "HKD",
    totalTransFee: "",
    totalNet: "",
    transDate: "",
    settleDate: "",
    paymentGateway: "",
    changeReason: "",
  });
  const [selectedArray, setSelectedArray] = useState([]);
  const [isMoreThanOneCurrency, setIsMoreThanOneCurrency] = useState(false);
  const CHANGE_ALL_CHECK_BOX_INITIAL_STATE = {
    isPlanStatusChecked: false,
    isSettleDateChecked: false,
  };
  const [changeAllCheckBoxFields, setChangeAllCheckBoxFields] = useState(
    CHANGE_ALL_CHECK_BOX_INITIAL_STATE,
  );
  const [dialogLoading, setDialogLoading] = useState(false);
  useEffect(() => {
    if (selectedArray && currencyKeyList) {
      setIsMoreThanOneCurrency(false);
      for (const key of currencyKeyList) {
        const tempArray = [];
        selectedArray.forEach((item) => {
          if (!tempArray.includes(item[key])) {
            tempArray.push(item[key]);
          }
        });
        if (tempArray.length > 1) {
          setIsMoreThanOneCurrency(true);
        }
      }
    }
  }, [selectedArray]);

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse == undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const handleQueryListUIResponse = (apiResponse) => {
    if (!apiResponse || !apiResponse?.data) {
      return snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.settlement.plan.sectionName")}` }), "error");
    }
    const { status, data: responseBody } = apiResponse;

    if (status === 400) {
      snackbar(
        t("general.message.search.error") + ": " + responseBody.message,
        "error",
      );
      setDatasource({ data: [], page: 0, total: 0 });
      setCurrentPage(0);
      setPageSize(20);
      Object.assign(pageSizeInfoRef.current, {
        currentpage: 0,
        pagesize: 20,
      });
      return;
    }

    if (status === 401) {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    }

    if (status === 403) {
      snackbar(t("general.message.unauthorized"), "error");
      return;
    }

    switch (responseBody.responseCode) {
      case "0000":
        if (responseBody?.data?.length === 0) {
          snackbar(t("general.message.no.result"), "warning");
        }
        setDatasource(responseBody);
        setCurrentPage(responseBody.page);
        setPageSize(responseBody.size);
        Object.assign(pageSizeInfoRef.current, {
          currentpage: responseBody.page,
          pagesize: responseBody.size,
        });
        break;

      default:
        snackbar(t("general.message.networkError"), "error");
        break;
    }
  };

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryTableData(queryList, {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    handleQueryListUIResponse(apiResponse);
    setSearch(false);
    setLoading(false);
  };

  useEffect(() => {
    if (currentPath === path && detailPageState?.[path]?.refresh) {
      redirectFn("signal", { path, state: { refresh: false } });
      if (!loading) initialFetch();
    }
  }, [currentPath]);

  const resetInputField = () => {
    setSearchFieldInput({
      id: "",
      payfacPrefix: "",
      merchantId: "",
      merchantName: "",
      bankAccount: "",
      beneficiary: "",
      currency: "",
      totalAmount: "",
      settleCurrency: "",
      adjustAmount: "",
      totalNet: "",
      startDate: null,
      endDate: null,
      startSettleDate: null,
      endSettleDate: null,
      cardType: "",
      cardOrigin: "",
      paymentGateway: "",
      status: "",
    });
  };

  const resetError = () => {
    setError({
      startDate: false,
      endDate: false,
      startDateLaterThanEndDate: false,
      startSettleDate: false,
      endSettleDate: false,
      settlementStartDateLaterThanEndDate: false,
      planStatus: false,
      settleDate: false,
      settleDateBeforeToday: false,
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    resetError();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            startDateLaterThanEndDate: true,
          },
        (input) =>
          ((input.startSettleDate && !input.endSettleDate) ||
            input.endSettleDate === "") && { endSettleDate: true },
        (input) =>
          ((input.endSettleDate && !input.startSettleDate) ||
            input.startSettleDate === "") && { startSettleDate: true },
        (input) =>
          moment(input.startSettleDate).isAfter(input.endSettleDate) && {
            settlementStartDateLaterThanEndDate: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryTableData(queryList, payload);
    handleQueryListUIResponse(apiResponse);
    setSearch(true);
    setSearching(false);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const payload = trimObject({ ...searchFieldInput });
    const apiResponse = await exportCsv(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.export.function.success.message"),
              "success",
            );
            const url = responseBody.data;
            window.location = url;
          } else {
            snackbar(
              t("general.message.export.function.fail.message"),
              "error",
            );
          }
        }
      }
    }
    setExporting(false);
  };

  // ===================== change funciton ===================== //

  const openChangeDialog = (data) => {
    setChangeDialogData({
      ...changeDialogData,
      adjustAmount: data.adjustAmount,
      settleDate: data.settleDate,
      changeReason: data.changeReason,
      id: data.id,
    });
    setOnChangeData(data);
    setIsChangeDialogOpen(true);
    setDialogLoading(false);
  };

  const closeChangeDialog = () => {
    restoreChangeFormInput();
    setIsChangeDialogOpen(false);
    setDialogLoading(false);
  };

  const handleChangeFormInput = (data, field) => {
    setChangeDialogData({
      ...changeDialogData,
      [field]: data,
    });
  };

  const restoreChangeFormInput = () => {
    setChangeDialogData({
      adjustAmount: "",
      updateBankAccount: false,
      settleDate: "",
      changeReason: "",
    });
  };

  const changeContent = () => {
    return (
      <ChangeContent handleInput={handleChangeFormInput} data={onchangeData} />
    );
  };

  const handleChangeAction = async () => {
    const payload = trimObject({ ...changeDialogData });
    const apiResponse = await changePlan(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.change.function.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("general.message.change.function.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmChange = async () => {
    if (!changeDialogData.adjustAmount && changeDialogData.adjustAmount !== 0) {
      snackbar(
        t("page.finance.settlement.plan.all.change.function.error.message.1"),
        "warning",
      );
      return;
    }
    if (isNaN(+changeDialogData.adjustAmount)) {
      snackbar(
        t("page.finance.settlement.plan.all.change.function.error.message.2"),
        "warning",
      );
      return;
    }
    if (!changeDialogData.settleDate) {
      snackbar(
        t("page.finance.settlement.plan.all.change.function.error.message.3"),
        "warning",
      );
      return;
    }
    if (!changeDialogData.changeReason) {
      snackbar(
        t("page.finance.settlement.plan.all.change.function.error.message.4"),
        "warning",
      );
      return;
    }
    setDialogLoading(true);
    const result = await handleChangeAction();
    closeChangeDialog();
    if (result) {
      return handleSearch("search");
    }
  };

  // ===================== change all funciton ===================== //

  const openChangeAllDialog = () => {
    if (selectedArray.length === 0) {
      snackbar(
        t(
          "page.finance.settlement.plan.all.change.all.function.error.message.2",
        ),
        "warning",
      );
      return;
    }
    setChangeAllDialogData({
      ...changeAllDialogData,
    });
    setChangeAllCheckBoxFields(CHANGE_ALL_CHECK_BOX_INITIAL_STATE);
    setError({
      ...error,
      planStatus: false,
      settleDate: false,
      settleDateBeforeToday: false,
    });
    setIsChangeAllDialogOpen(true);
    setDialogLoading(false);
  };

  const closeChangeAllDialog = () => {
    setDialogLoading(false);
    restoreChangeAllFormInput();
    setIsChangeAllDialogOpen(false);
  };

  const handleChangeAllFormInput = (data, field) => {
    setChangeAllDialogData({
      ...changeAllDialogData,
      [field]: data,
    });
  };

  const restoreChangeAllFormInput = () => {
    setChangeAllDialogData({
      updateBankAccount: false,
      settleDate: "",
      planStatus: "",
    });
  };

  const changeAllContent = () => {
    return (
      <ChangeAllContent
        handleInput={handleChangeAllFormInput}
        changeAllDialogData={changeAllDialogData}
        error={error}
        checkBoxFields={changeAllCheckBoxFields}
        setCheckBoxFields={setChangeAllCheckBoxFields}
      />
    );
  };

  const handleChangeAllAction = async () => {
    const payload = {
      ids: selectedArray.map((row) => row.id),
      ...Object.fromEntries(
        Object.entries(changeAllDialogData).filter(([, value]) => value !== ""),
      ),
    };
    const apiResponse = await changeAllPlan(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t(
                "page.finance.settlement.plan.all.change.all.function.success.message",
              ),
              "success",
            );
            return true;
          } else {
            snackbar(
              t(
                "page.finance.settlement.plan.all.change.all.function.fail.message",
              ),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmChangeAll = async () => {
    // Validation for changeAllDialog before calling api
    const { errorObj, hasError } = searchErrorHandler(changeAllDialogData, [
      (input) =>
        input.settleDate === "" &&
        changeAllCheckBoxFields.isSettleDateChecked && {
          settleDate: true,
        },
      (input) =>
        input.planStatus === "" &&
        changeAllCheckBoxFields.isPlanStatusChecked && {
          planStatus: true,
        },
    ]);
    setError(errorObj);
    if (hasError) return;
    setDialogLoading(true);
    const result = await handleChangeAllAction();
    closeChangeAllDialog();
    if (result) {
      handleSearch("search");
      setSelectedArray([]);
    }
  };

  // ===================== create funciton ===================== //

  const openCreateDialog = () => {
    setDialogLoading(false);
    setIsCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setDialogLoading(false);
    restoreCreateFormInput();
    setIsCreateDialogOpen(false);
  };

  const handleCreateFormInput = (data, field) => {
    setCreateDialogData({
      ...createDialogData,
      [field]: data,
    });
  };

  const restoreCreateFormInput = () => {
    setCreateDialogData({
      merchantId: "",
      totalServiceFee: "",
      totalChangeFee: "",
      settleCurrency: "HKD",
      totalTransFee: "",
      totalNet: "",
      transDate: "",
      settleDate: "",
      paymentGateway: "",
      changeReason: "",
    });
  };

  const createFormContent = () => {
    return <CreateFormContent handleInput={handleCreateFormInput} />;
  };

  const handleCreateFormAction = async () => {
    const payload = trimObject({ ...createDialogData });
    const apiResponse = await createForm(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.create.function.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("general.message.create.function.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmCreate = async () => {
    if (!createDialogData.merchantId) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.1"),
        "warning",
      );
      return;
    }
    if (!createDialogData.totalServiceFee) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.2"),
        "warning",
      );
      return;
    }
    if (!createDialogData.totalChangeFee) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.3"),
        "warning",
      );
      return;
    }
    if (!createDialogData.settleCurrency) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.4"),
        "warning",
      );
      return;
    }
    if (!createDialogData.totalTransFee) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.5"),
        "warning",
      );
      return;
    }
    if (!createDialogData.totalNet) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.6"),
        "warning",
      );
      return;
    }
    if (!createDialogData.transDate) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.9"),
        "warning",
      );
      return;
    }
    if (!createDialogData.paymentGateway) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.7"),
        "warning",
      );
      return;
    }
    if (!createDialogData.changeReason) {
      snackbar(
        t("page.finance.settlement.plan.all.create.function.error.message.8"),
        "warning",
      );
      return;
    }
    setDialogLoading(true);
    const result = await handleCreateFormAction();
    closeCreateDialog();
    if (result) {
      return handleSearch("search");
    }
  };

  const updateSelectedArray = (array) => {
    setSelectedArray(array);
  };

  const restoreNumberOfSelectedCurrency = () => {
    setIsMoreThanOneCurrency(false);
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const transactionDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startDate: false,
        startDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      setError({ ...error, endDate: false, startDateLaterThanEndDate: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    } else {
      return;
    }
  };
  const settlementDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startSettleDate: false,
        settlementStartDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startSettleDate: date });
    } else if (type === "end") {
      setError({
        ...error,
        endSettleDate: false,
        settlementStartDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, endSettleDate: date });
    } else {
      return;
    }
  };
  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const currencyKeyList = ["currency", "settleCurrency"];
  const columns = [
    {
      index: 0,
      key: "id",
      title: t("page.finance.settlement.plan.all.column.title.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    ...payfacPrefixColumn,
    {
      index: 1,
      key: "merchantId",
      title: t("page.finance.settlement.plan.all.column.title.merchant.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "merchantName",
      title: t("page.finance.settlement.plan.all.column.title.merchant.name"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "bankAccount",
      title: t("page.finance.settlement.plan.all.column.title.bank.account"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "beneficiary",
      title: t("page.finance.settlement.plan.all.column.title.beneficiary"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "currency",
      title: t(
        "page.finance.settlement.plan.all.column.title.transaction.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 6,
      key: "totalAmount",
      currencyKey: "currency",
      title: t("page.finance.settlement.plan.all.column.title.total.amount"),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 7,
      key: "fxRate",
      title: t("page.finance.settlement.plan.all.column.title.fx.rate"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 8,
      key: "totalCost",
      currencyKey: "currency",
      title: t("page.finance.settlement.plan.all.column.title.cost"),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 9,
      key: "allFee1",
      currencyKey: "currency",
      title: t("page.finance.settlement.plan.all.column.title.all.fee.1"),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 10,
      key: "totalServiceFee",
      currencyKey: "currency",
      title: t("page.finance.settlement.plan.all.column.title.service.fee"),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 11,
      key: "totalChangeFee",
      currencyKey: "currency",
      title: t(
        "page.finance.settlement.plan.all.column.title.foreigh.card.fee",
      ),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 12,
      key: "totalCampaignFee",
      currencyKey: "currency",
      title: t(
        "page.finance.settlement.plan.all.column.title.campaign.fee",
      ),
      indicator: " [1]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 13,
      key: "allFee2",
      currencyKey: "settleCurrency",
      title: t("page.finance.settlement.plan.all.column.title.all.fee.2"),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 14,
      key: "gatewayFee",
      currencyKey: "settleCurrency",
      title: t("page.finance.settlement.plan.all.column.title.gateway.fee"),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 15,
      key: "threeDSFee",
      currencyKey: "settleCurrency",
      title: t("page.finance.settlement.plan.all.column.title.3ds.fee"),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 16,
      key: "tokenFee",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.all.column.title.tokenization.fee",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 17,
      key: "totalTransFee",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.all.column.title.small.ticket.fee",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 18,
      key: "adjustAmount",
      currencyKey: "settleCurrency",
      title: t("page.finance.settlement.plan.all.column.title.adjust.amount"),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 19,
      key: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.all.column.title.settlement.currency",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 20,
      key: "totalNet",
      currencyKey: "settleCurrency",
      title: t("page.finance.settlement.plan.all.column.title.net.amount"),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 21,
      key: "totalNetInternal",
      currencyKey: "settleCurrency",
      title: t(
        "page.finance.settlement.plan.all.column.title.net.amount.internal",
      ),
      indicator: " [2]",
      type: "amount",
      sorter: false,
      summary: true,
      render: (data, key, column) => {
        return checkMoneyDataForTable(data, key, column.currencyKey);
      },
    },
    {
      index: 22,
      key: "transDate",
      title: t(
        "page.finance.settlement.plan.all.column.title.transaction.date",
      ),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 23,
      key: "settleDate",
      title: t("page.finance.settlement.plan.all.column.title.settlement.date"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 24,
      key: "cardType",
      title: t("page.finance.settlement.plan.all.column.title.card.type"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 25,
      key: "cardOrigin",
      title: t("page.finance.settlement.plan.all.column.title.card.origin"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 26,
      key: "paymentGateway",
      title: t("page.finance.settlement.plan.all.column.title.gateway"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 27,
      key: "changeReason",
      title: t("page.finance.settlement.plan.all.column.title.reason"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 28,
      key: "status",
      title: t("page.finance.settlement.plan.all.column.title.status"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 29,
      key: "isSubmitted",
      title: t("page.finance.settlement.plan.all.column.title.submitted"),
      sorter: false,
      render: (data, key) => {
        return data[key] ? "Yes" : "No";
      },
    },
    {
      index: 30,
      key: "action",
      title: t("page.finance.settlement.plan.all.column.title.action"),
      sorter: false,
      render: (data) => {
        return (
          <div className={s.actionBtnContainer}>
            <ButtonMAS variant="text-only" overlayLoading onClick={() => openChangeDialog(data)}>
              {t("page.finance.settlement.plan.all.column.title.action.1")}
            </ButtonMAS>
          </div>
        );
      },
    },
  ];

  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    const statusList = ["approved", "waiting", "rejected"];
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <OutlinedText
            label={t("page.finance.settlement.plan.all.id.label")}
            value={searchFieldInput.id}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "id")}
            onfocusFn={() => textInputOnFocus("id")}
            onblurFn={() => textInputOnBlur("id")}
            sxParams={{ textFocus: focus.id }}
          />
          <PayfacPrefixSearchFilter
            searchFieldInput={searchFieldInput}
            setSearchFieldInput={setSearchFieldInput}
          />
          <OutlinedText
            label={t("page.finance.settlement.plan.all.merchant.id.label")}
            value={searchFieldInput.merchantId}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantId")}
            onfocusFn={() => textInputOnFocus("merchantId")}
            onblurFn={() => textInputOnBlur("merchantId")}
            sxParams={{ textFocus: focus.merchantId }}
          />
          <OutlinedText
            label={t("page.finance.settlement.plan.all.merchant.name.label")}
            value={searchFieldInput.merchantName}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "merchantName")}
            onfocusFn={() => textInputOnFocus("merchantName")}
            onblurFn={() => textInputOnBlur("merchantName")}
            sxParams={{ textFocus: focus.merchantName }}
          />
          <OutlinedText
            label={t("page.finance.settlement.plan.all.bank.account.label")}
            value={searchFieldInput.bankAccount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "bankAccount")}
            onfocusFn={() => textInputOnFocus("bankAccount")}
            onblurFn={() => textInputOnBlur("bankAccount")}
            sxParams={{ textFocus: focus.bankAccount }}
          />
          <OutlinedText
            label={t("page.finance.settlement.plan.all.beneficiary.label")}
            value={searchFieldInput.beneficiary}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "beneficiary")}
            onfocusFn={() => textInputOnFocus("beneficiary")}
            onblurFn={() => textInputOnBlur("beneficiary")}
            sxParams={{ textFocus: focus.beneficiary }}
          />
          <Selection
            label={t(
              "page.finance.settlement.plan.all.transaction.currency.label",
            )}
            selectionValue={searchFieldInput.currency}
            onchangeFn={(e) => textInputOnchange(e, "currency")}
            onfocusFn={() => textInputOnFocus("currency")}
            onblurFn={() => textInputOnBlur("currency")}
            options={transactionCurrencyOptions("all")}
            sxParams={{ selectionFocus: focus.currency }}
          />
          <OutlinedText
            label={t("page.finance.settlement.plan.all.total.amount.label")}
            value={searchFieldInput.totalAmount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "totalAmount")}
            onfocusFn={() => textInputOnFocus("totalAmount")}
            onblurFn={() => textInputOnBlur("totalAmount")}
            sxParams={{ textFocus: focus.totalAmount }}
          />
          <Selection
            label={t(
              "page.finance.settlement.plan.all.settlement.currency.label",
            )}
            selectionValue={searchFieldInput.settleCurrency}
            onchangeFn={(e) => textInputOnchange(e, "settleCurrency")}
            onfocusFn={() => textInputOnFocus("settleCurrency")}
            onblurFn={() => textInputOnBlur("settleCurrency")}
            options={transactionCurrencyOptions(["HKD", "USD"])}
            sxParams={{ selectionFocus: focus.settleCurrency }}
          />
          <OutlinedText
            label={t("page.finance.settlement.plan.all.adjust.amount.label")}
            value={searchFieldInput.adjustAmount}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "adjustAmount")}
            onfocusFn={() => textInputOnFocus("adjustAmount")}
            onblurFn={() => textInputOnBlur("adjustAmount")}
            sxParams={{ textFocus: focus.adjustAmount }}
          />
          <OutlinedText
            label={t("page.finance.settlement.plan.all.net.amount.label")}
            value={searchFieldInput.totalNet}
            onEnterDownFn={handleSearch}
            onchangeFn={(e) => textInputOnchange(e, "totalNet")}
            onfocusFn={() => textInputOnFocus("totalNet")}
            onblurFn={() => textInputOnBlur("totalNet")}
            sxParams={{ textFocus: focus.totalNet }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.all.transaction.start.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.all.transaction.start.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.all.transaction.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.startDate}
            limitValue={searchFieldInput.endDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.all.transaction.end.date.label",
            )}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.all.transaction.end.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.all.transaction.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.endDate}
            limitValue={searchFieldInput.startDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError: error.endDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.all.settlement.start.date.label",
            )}
            errorMessage={
              error.settlementStartDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.all.settlement.start.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.all.settlement.start.date.error.message",
                  )
            }
            type="start"
            value={searchFieldInput.startSettleDate}
            limitValue={searchFieldInput.endSettleDate}
            onchangeFn={settlementDateOnChange}
            onfocusFn={() => textInputOnFocus("startSettleDate")}
            onblurFn={() =>
              textInputOnBlur("startSettleDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.startSettleDate,
              datepickerError:
                error.startSettleDate ||
                error.settlementStartDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t(
              "page.finance.settlement.plan.all.settlement.end.date.label",
            )}
            errorMessage={
              error.settlementStartDateLaterThanEndDate
                ? t(
                    "page.finance.settlement.plan.all.settlement.end.date.error.message.2",
                  )
                : t(
                    "page.finance.settlement.plan.all.settlement.end.date.error.message",
                  )
            }
            type="end"
            value={searchFieldInput.endSettleDate}
            limitValue={searchFieldInput.startSettleDate}
            onchangeFn={settlementDateOnChange}
            onfocusFn={() => textInputOnFocus("endSettleDate")}
            onblurFn={() =>
              textInputOnBlur("endSettleDate", dateFormatValidator)
            }
            sxParams={{
              datepickerFocus: focus.endSettleDate,
              datepickerError:
                error.endSettleDate ||
                error.settlementStartDateLaterThanEndDate,
            }}
          />
          <Selection
            label={t("page.finance.settlement.plan.all.card.type.label")}
            selectionValue={searchFieldInput.cardType}
            onchangeFn={(e) => textInputOnchange(e, "cardType")}
            onfocusFn={() => textInputOnFocus("cardType")}
            onblurFn={() => textInputOnBlur("cardType")}
            options={cardTypeOptions(
              (e) => !["loyalty", "octopus"].includes(e.key),
            )}
            sxParams={{ selectionFocus: focus.cardType }}
          />
          <Selection
            label={t("page.finance.settlement.plan.all.card.origin.label")}
            selectionValue={searchFieldInput.cardOrigin}
            onchangeFn={(e) => textInputOnchange(e, "cardOrigin")}
            onfocusFn={() => textInputOnFocus("cardOrigin")}
            onblurFn={() => textInputOnBlur("cardOrigin")}
            options={cardOriginOptions("all")}
            sxParams={{ selectionFocus: focus.cardOrigin }}
          />
          <Selection
            label={t("page.finance.settlement.plan.all.gateway.label")}
            selectionValue={searchFieldInput.paymentGateway}
            onchangeFn={(e) => textInputOnchange(e, "paymentGateway")}
            onfocusFn={() => textInputOnFocus("paymentGateway")}
            onblurFn={() => textInputOnBlur("paymentGateway")}
            options={paymentGatewayOptions("settlement")}
            sxParams={{ selectionFocus: focus.paymentGateway }}
          />
          <Selection
            label={t("page.finance.settlement.plan.all.status.label")}
            selectionValue={searchFieldInput.status}
            onchangeFn={(e) => textInputOnchange(e, "status")}
            onfocusFn={() => textInputOnFocus("status")}
            onblurFn={() => textInputOnBlur("status")}
            options={statusOptions(statusList)}
            sxParams={{ selectionFocus: focus.status }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching || exporting}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <div className={s.descriptionBarContainer}>
          <div className={s.descriptionBar}>
            <span>{t("component.remarks.label")}</span>
            <span>{t("page.finance.settlement.plan.all.description.1")}</span>
            <span>{t("page.finance.settlement.plan.all.description.2")}</span>
          </div>
          <FunctionalButtonBar
            loading={loading || searching || exporting}
            selectedItemNumber={selectedArray.length}
            exporting={exporting}
            createBtn
            handleCreate={openCreateDialog}
            changeAllBtn={selectedArray.length !== 0}
            handleChangeAll={openChangeAllDialog}
            exportBtn
            handleExport={handleExportCsv}
            refreshBtn
            handleRefresh={handleSearch}
            columnDisplayBtn
            columnDisplayProps={{
              columns,
              columnsArray,
              setColumnsArray,
              rememberPreference: "finance-settlement-all-payfac-prefix",
            }}
          />
        </div>
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
              checkbox
              selectedArray={selectedArray}
              updateSelectedArrayFn={updateSelectedArray}
              summaryTable
              isMoreThanOneCurrency={isMoreThanOneCurrency}
              restoreNumberOfSelectedCurrencyFn={
                restoreNumberOfSelectedCurrency
              }
            />
          )}
          {searching && <Spin type={"search"} />}
        </div>
        <SimpleDialog
          open={isChangeDialogOpen}
          dialogOncloseFn={closeChangeDialog}
          title={t(
            "page.finance.settlement.plan.all.change.function.dialog.title",
          )}
          content={changeContent()}
          confirmFn={confirmChange}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
        <SimpleDialog
          open={isChangeAllDialogOpen}
          dialogOncloseFn={closeChangeAllDialog}
          title={t(
            "page.finance.settlement.plan.all.change.all.function.dialog.title",
          )}
          content={changeAllContent()}
          confirmFn={confirmChangeAll}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          positiveActionDisabled={
            changeAllDialogData.settleDate === "" &&
            changeAllDialogData.planStatus === "" &&
            !changeAllDialogData.updateBankAccount
          }
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
        <SimpleDialog
          open={isCreateDialogOpen}
          dialogOncloseFn={closeCreateDialog}
          title={t(
            "page.finance.settlement.plan.all.create.function.dialog.title",
          )}
          content={createFormContent()}
          confirmFn={confirmCreate}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
      </div>
    </div>
  );
};

export default React.memo(All);
